import { Layout, Row, Col, Space, Typography, Divider } from "antd";
import { MailOutlined, PhoneOutlined, EnvironmentOutlined } from "@ant-design/icons";
import { BrandLogo, useBrandToken } from "@repo/ui";

const { Footer } = Layout;
const { Text, Link, Title, Paragraph } = Typography;

type LinkItem = {
    label: string;
    href: string;
    external?: boolean;
};

type LinkGroup = {
    title: string;
    items: LinkItem[];
};

type Badge = {
    src: string; // путь к изображению (svg/png/webp)
    alt: string; // alt-текст
    href?: string; // клик ведёт на страницу
    tooltip?: string; // текст при hover
    width?: number; // при необходимости зафиксировать размеры
    height?: number;
};

type BrandInfo = {
    name: string;
    tagline?: string;
    email?: string;
    phone?: string;
    address?: string;
};

export type CustomFooterProps = {
    brand: BrandInfo;
    links?: LinkGroup[];
    badges?: Badge[];
    year?: number;
};

const DEFAULT_LINKS: LinkGroup[] = [
    {
        title: "Продукт",
        items: [
            { label: "Тарифы", href: "/pricing" },
            // { label: "Пользовательское соглашение", href: "/terms" },
            // { label: "Политика конфиденциальности", href: "/privacy" },
            // { label: "API документация", href: "/api" },
        ],
    },
    {
        title: "Ресурсы",
        items: [
            { label: "База знаний", href: "/docs" },
            // { label: "Плагин для браузера", href: "/extensions" },
            // { label: "Партнёрская программа", href: "/partners" },
            // { label: "Контакты", href: "/contacts" },
        ],
    },
];

export default function CustomFooter({
    brand,
    links = DEFAULT_LINKS,
    year = new Date().getFullYear(),
}: CustomFooterProps) {
    const token = useBrandToken();

    return (
        <Footer className="border-t !px-8 !py-4">
            <div className="flex justify-center">
                {/* Левая колонка — Бренд */}

                <Space align="start" size="large">
                    <Space direction="vertical" size={8}>
                        <BrandLogo src="/logo.svg" height={60} width={150} />
                        <Paragraph style={{ color: token.colorTextTertiary }}>
                            Платформа аналитики и управления продажами на маркетплейсах. Сложные
                            задачи бизнеса — в простые решения.
                        </Paragraph>
                        {brand.tagline && <Text type="secondary">{brand.tagline}</Text>}
                        <Space direction="vertical" size={4}>
                            {brand.email && (
                                <Text>
                                    <MailOutlined />{" "}
                                    <Link href={`mailto:${brand.email}`}>{brand.email}</Link>
                                </Text>
                            )}
                            {brand.phone && (
                                <Text>
                                    <PhoneOutlined />{" "}
                                    <Link href={`tel:${brand.phone}`}>{brand.phone}</Link>
                                </Text>
                            )}
                            {brand.address && (
                                <Text>
                                    <EnvironmentOutlined /> {brand.address}
                                </Text>
                            )}
                        </Space>
                    </Space>
                </Space>

                {/* Средняя колонка — Полезные ссылки */}

                <Row gutter={[24, 8]}>
                    {links.map((group) => (
                        <Col xs={12} key={group.title}>
                            <Title level={5} style={{ marginBottom: 8 }}>
                                {group.title}
                            </Title>
                            <Space direction="vertical" size={6}>
                                {group.items.map((item) => (
                                    <Link
                                        key={item.label}
                                        href={item.href}
                                        target={item.external ? "_blank" : undefined}
                                        rel={item.external ? "noopener noreferrer" : undefined}
                                    >
                                        {item.label}
                                    </Link>
                                ))}
                            </Space>
                        </Col>
                    ))}
                </Row>
            </div>

            <Divider style={{ margin: 8 }} />

            {/* Нижняя плашка */}
            <div
                style={{
                    maxWidth: 1280,
                    margin: "0 auto",
                    padding: "12px 16px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                    color: token.colorTextTertiary,
                    fontSize: 12,
                }}
            >
                <Text>
                    © {year} {brand.name}. Все права защищены.
                </Text>
                <Space size="middle" wrap>
                    <Link href="/security">Безопасность</Link>
                    <Link href="/compliance">Соответствие</Link>
                    <Link href="/status">Статус сервиса</Link>
                </Space>
            </div>
        </Footer>
    );
}
